import { Fragment, useCallback, useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import dayjs from 'dayjs';
import ExpertFilterForm from './ProjectsFilterForm';
import { Layout, Table, Spin, Avatar, Button, message, Flex, Space, Tooltip } from 'antd';
import { fullPagerConfig } from '@/utils/property';
import { getOnlineDailyApi, downloadStatisticsData } from '@services/Mdm';
import { getFileUrl, projectType, eventType } from './utils';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import './index.less';
const Projects = () => {
    // 渲染table数据的state
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [filterObj, setFilterObj] = useState({});

    // 分页设置
    const [pageSize, setPageSize] = useState(10);
    const [currPage, setCurrPage] = useState(1);
    const lastParamsRef = useRef();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.projects-table-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 筛选数据
    const handleFilterFinish = value => {
        const newValue = { ...value };
        if (newValue.onlineTime) {
            newValue.startDate = newValue.onlineTime[0].format('YYYY-MM-DD HH:mm:ss');
            newValue.endDate = newValue.onlineTime[1].format('YYYY-MM-DD HH:mm:ss');
            delete newValue.onlineTime;
        }
        setFilterObj(newValue);
        setCurrPage(1);
    };

    // 改变一页有多少数据
    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    // 改变第几页
    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    const [pedding, setPedding] = useState(false);

    // 获取用户列表
    const getOnlineDailyList = async () => {
        setPedding(true);
        const params = {
            ...filterObj,
            pageSize,
            page: currPage,
        };
        const res = await getOnlineDailyApi(params);
        lastParamsRef.current = params;
        setPedding(false);
        if (res && res.status_code === 200) {
            setTableData(res.data.list);
            setTotal(res.data.totalCount);
        }
    };

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: total,
        onShowSizeChange: (current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    // 导出数据
    const handleExportFile = () => {
        if (tableData.length === 0) {
            return message.warning('当前列表没有数据，无法导出');
        }
        message.success('请稍后，正在导出');
        downloadStatisticsData(lastParamsRef.current).then(res => {
            if (res.size !== 0) {
                const fileName = `参与项目统计_${dayjs().format('YYYYMMDDTHHmmss')}.xlsx`;
                const a = document.createElement('a');
                a.download = fileName;
                const objectUrl = URL.createObjectURL(res);
                a.href = objectUrl;
                a.click();
                setTimeout(() => {
                    URL.revokeObjectURL(objectUrl);
                }, 1000);
            } else {
                message.warning('当前列表没有数据，无法导出');
            }
        });
    };

    const onRefresh = () => {
        getOnlineDailyList();
    };

    // table 数据相关
    const columns = useMemo(() => {
        return [
            {
                title: '用户昵称',
                key: 'id',
                render: (state, row) => (
                    <>
                        <Avatar src={getFileUrl(row.avatar)} icon={!row.avatar && <i className="bi bi-person"></i>} />
                        <span style={{ marginLeft: '5px' }}>{row.nickName}</span>
                    </>
                ),
            },
            { title: '手机号', dataIndex: 'phone' },
            {
                title: '项目类型',
                key: 'code',
                render: (state, row) => projectType[row.code],
            },
            { title: '项目名称', dataIndex: 'projectName' },
            { title: '项目ID', dataIndex: 'projectId' },
            { title: '用户事件', key: 'event', render: (_, row) => eventType[row.event] },
            { title: '事件记录时间', dataIndex: 'onlineAt' },
        ];
    }, []);

    // 渲染table数据
    useEffect(() => {
        getOnlineDailyList();
    }, [pageSize, currPage, filterObj]);

    return (
        <div className="page-container projects-page">
            <PageHeader title="医疗决策人参与项目统计" breadcrumb={[{ label: '用户中心', path: '/uc' }, { label: '医疗决策人管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <div className="projects-filter-panel">
                    <ExpertFilterForm handleFilterFinish={handleFilterFinish} />
                </div>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="projects-table-region">
                <div className="main-toolbar projects-table-toolbar">
                    <div className="main-tools">
                        <Button type="default" onClick={handleExportFile} icon={<i className="bi bi-download"></i>}>
                            导出
                        </Button>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={onRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="projects-table-wrap">
                    <Spin spinning={pedding} delay={500}>
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            bordered
                            size="medium"
                            columns={columns}
                            dataSource={tableData}
                            rowKey="id"
                            scroll={{
                                x: 'max-content',
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                            sticky
                            pagination={paginationProps}
                        />
                    </Spin>
                </div>
            </div>
        </div>
    );
};

export default Projects;
